import type {
  ConversationSummary,
  CreateConversationOptions,
  RemoteMessage,
  SendMessageOptions,
  Skill,
  SkillGroup,
  ToolCall,
  Turn,
} from '../types';
import { AgentClient, canonicalPagePathname } from './AgentClient';
import { STEP_DELAY, TOKEN_DELAY, buildResponse, uid } from './mock/scenarios';

interface ScheduledTurn extends Turn {
  cancel: () => void;
}

const MOCK_SKILLS: Skill[] = [
  {
    name: 'design-landing-page',
    title: 'Landing Page aufbauen',
    group: 'layout',
    position: 0,
    description:
      'Bereichs-Einstiegsseite (Landing Page) aufbauen: Struktur, Navigation und visuelle Hierarchie.',
  },
  {
    name: 'design-review',
    title: 'Design-Review',
    group: 'layout',
    position: 1,
    description:
      'Design-Review: Seitenlayout auf visuelle Monotonie, fehlende Abwechslung und Strukturprobleme prüfen.',
  },
  {
    name: 'extract-from-document',
    title: 'Aus Dokument übernehmen',
    group: 'text',
    position: 2,
    description:
      'Inhalte aus einem Dokument im Portal extrahieren und strukturiert in die Seite einarbeiten.',
  },
  {
    name: 'improve-text-flow',
    title: 'Textfluss verbessern',
    group: 'text',
    position: 3,
    description:
      'Textfluss und Lesbarkeit verbessern: Fließtext durch gezielte Formatierung auflockern.',
  },
  {
    name: 'proofread',
    title: 'Korrekturlesen',
    group: 'text',
    position: 4,
    description:
      'Korrekturlesen: Rechtschreibung, Grammatik, Zeichensetzung und Stilkonsistenz prüfen.',
  },
  {
    name: 'rewrite-tone',
    title: 'Tonalität ändern',
    group: 'text',
    position: 5,
    description:
      'Tonalität ändern: Alle Texte der Seite in einen anderen Sprachstil umschreiben.',
  },
  {
    name: 'seo-check',
    title: 'SEO-Check',
    group: null,
    position: 6,
    description:
      'SEO-Check: Titel, Meta-Description, Alt-Texte und Heading-Hierarchie prüfen.',
  },
  {
    name: 'simplify-language',
    title: 'Einfache Sprache',
    group: 'text',
    position: 7,
    description:
      'Einfache Sprache: kurze Sätze, klare Wörter, verständliche Struktur.',
  },
  {
    name: 'site-consistency',
    title: 'Konsistenz prüfen',
    group: null,
    position: 8,
    description:
      'Konsistenzprüfung: die Seite mit ihren Geschwisterseiten vergleichen.',
  },
];

const MOCK_SKILL_GROUPS: SkillGroup[] = [
  { name: 'text', title: 'Text', position: 0 },
  { name: 'layout', title: 'Layout', position: 1 },
];

/**
 * The demo without a backend.
 *
 * A turn is a stream of events: tool calls interleaved with narration text.
 * Narration between tool calls and the final reply are the same thing — the
 * model emits text and only afterwards decides whether more tool calls
 * follow, so no event is knowable as "the reply" until the turn ends. The
 * pacing is faked with timers; everything else the UI sees is the shape the
 * real client produces, which is what makes the two interchangeable.
 */
export class MockAgentClient extends AgentClient {
  async createConversation({
    pathname,
    language,
  }: CreateConversationOptions): Promise<{ conversationId: string }> {
    const conversationId = uid();
    this.current = {
      conversationId,
      pathname: canonicalPagePathname(pathname),
      language,
    };

    return { conversationId };
  }

  async listConversations(): Promise<ConversationSummary[]> {
    return [];
  }

  /**
   * The same names the agent ships, so the offline demo shows the menu the
   * real one does. The scenarios do not act on them.
   */
  async listSkills(): Promise<Skill[]> {
    return MOCK_SKILLS;
  }

  async listSkillGroups(): Promise<SkillGroup[]> {
    return MOCK_SKILL_GROUPS;
  }

  async getConversationMessages(): Promise<RemoteMessage[]> {
    return [];
  }

  async renameConversation(
    conversationId: string,
    title: string,
  ): Promise<ConversationSummary> {
    return {
      id: conversationId,
      title,
      pathname: this.current?.pathname || '/',
      language: this.current?.language || 'de',
      createdAt: '',
      updatedAt: '',
      messageCount: 0,
      preview: '',
      running: false,
    };
  }

  async deleteConversation(): Promise<unknown> {
    return { status: 'deleted' };
  }

  sendMessage(options: SendMessageOptions): ScheduledTurn {
    const response = buildResponse({
      message: options.message,
      state: options.state,
      context: options.context,
      attachments: options.attachments,
      references: options.references,
      permission: options.permission,
    });

    // Scenarios either provide the interleaved event stream directly, or the
    // classic steps + reply, which normalise to tools followed by one text
    // segment.
    const events = response.events || [
      ...response.steps.map((step: ToolCall) => ({ kind: 'tool', step })),
      { kind: 'text', text: response.reply },
    ];

    const timers: ReturnType<typeof setTimeout>[] = [];
    let cancelled = false;
    const schedule = (delay: number, callback: () => void) =>
      timers.push(
        setTimeout(() => {
          if (!cancelled) callback();
        }, delay),
      );

    let clock = 450;
    let segment = 0;
    events.forEach((event: any) => {
      if (event.kind === 'tool') {
        schedule(clock, () => options.onToolCall(event.step));
        clock += event.step.duration || STEP_DELAY;

        return;
      }
      // Stream this text segment word by word, cumulative per segment. The
      // segment index travels along so consecutive segments — nothing but a
      // beat of thinking between them — stay distinct on the host side.
      clock += 250;
      const segmentIndex = segment;
      segment += 1;
      let streamed = '';
      event.text.split(/(?<=\s)/).forEach((token: string) => {
        streamed += token;
        const snapshot = streamed;
        schedule(clock, () => options.onChunk?.(snapshot, segmentIndex));
        clock += TOKEN_DELAY;
      });
      // A beat of "thinking" before the agent reaches for the next tool.
      clock += 400;
    });

    schedule(clock + 150, () =>
      options.onDone({
        newState: response.newState || null,
        denied: !!response.denied,
      }),
    );

    return {
      cancel: () => {
        cancelled = true;
        timers.forEach(clearTimeout);
      },
    };
  }
}
